import React, { ReactElement, memo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Cell, FormControl } from '@dexma/ui-components';
import i18n from 'asset/translation';
import FormSubmitModal from 'adapter/in/common/components/FormSubmitModal/FormSubmitModal';
import { ConfirmButtonGroupsDataIdContext } from 'adapter/in/common/components/ConfirmButtonGroup/ConfirmButtonGroup';
import { StyledModalLabelParagraph } from 'adapter/in/common/components/ConfirmActionModal/style';

export const NAME_MAX_LENGTH = 140;

export type CreateNameFormData = {
    name: string;
};

export type CreateModalProps = {
    error: boolean;
    success: boolean;
    isLoading: boolean;
    showModal: boolean;
    handleCloseModal: () => void;
    handleOnSubmit: (data: CreateNameFormData) => void;
    defaultValue?: string;
    title: string;
    dataIdContext: ConfirmButtonGroupsDataIdContext & { inputName: string; tryAgain: string };
    message: string;
    placeHolder?: string;
    proceedButtonText?: string;
    handleOnSkip?: () => void;
};

const SingleNameInputModal = memo(
    (props: CreateModalProps): ReactElement => {
        const {
            showModal,
            handleCloseModal,
            error,
            success,
            isLoading,
            handleOnSubmit: handleExternalSubmit,
            defaultValue,
            title,
            message,
            placeHolder,
            proceedButtonText,
            dataIdContext,
            handleOnSkip,
        } = props;

        const { handleSubmit, reset: formReset, control, setValue } = useForm<CreateNameFormData>();

        useEffect(() => {
            if (showModal && !defaultValue) {
                formReset();
            }
            if (showModal && defaultValue) {
                setValue('name', defaultValue);
            }
        }, [showModal, defaultValue]);

        const handleOnSubmit = () => {
            handleSubmit(handleExternalSubmit)();
        };

        return (
            <FormSubmitModal
                isLoading={isLoading}
                showModal={showModal}
                handleCloseModal={handleCloseModal}
                title={title}
                error={error}
                success={success}
                dataIdContext={dataIdContext}
                handleOnSubmit={handleOnSubmit}
                proceedButtonText={proceedButtonText}
                handleOnSkip={handleOnSkip}
            >
                <>
                    <Cell xs={12}>
                        <StyledModalLabelParagraph>{message} </StyledModalLabelParagraph>
                    </Cell>
                    <Cell xs={12}>
                        <Controller
                            control={control}
                            rules={{
                                required: { value: true, message: i18n.t('dashboards.create.noNameProvided') },
                                maxLength: { value: NAME_MAX_LENGTH, message: i18n.t('dashboards.create.nameTooLong') },
                            }}
                            defaultValue={defaultValue}
                            name='name'
                            render={({ field: { onChange, onBlur, value }, fieldState: { error: fieldError } }) => (
                                <FormControl
                                    data-testid='input-name'
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    control='Input'
                                    disabled={isLoading || success}
                                    value={value ?? ''}
                                    placeholder={placeHolder}
                                    message={fieldError?.message}
                                    error={!!fieldError}
                                    dataId={props.dataIdContext.inputName}
                                    inputLabel={message}
                                />
                            )}
                        />
                    </Cell>
                </>
            </FormSubmitModal>
        );
    }
);

export default SingleNameInputModal;
